import { useSearchParams, useNavigate } from "react-router-dom";
import { PageTransition } from "@/components/PageTransition";
import { SearchBar } from "@/components/SearchBar";
import { TermCard } from "@/components/TermCard";
import { useTerms } from "@/hooks/useTerms";
import { Search as SearchIcon, ArrowLeft, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";
import { useLanguage } from "@/contexts/LanguageContext";

const Search = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const {
    data: terms,
    isLoading,
    error,
    refetch,
  } = useTerms({ search: query });

  return (
    <PageTransition>
      <div className="min-h-screen">
        {/* Hero Header */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(220,40%,13%)] via-[hsl(220,38%,18%)] to-[hsl(218,35%,22%)]">
          {/* Animated Background */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="bg-primary/20 float absolute -right-20 -top-20 h-60 w-60 rounded-full blur-3xl" />
            <div className="bg-gold/15 float-delayed absolute -bottom-20 -left-20 h-80 w-80 rounded-full blur-3xl" />
          </div>

          {/* Decorative Pattern */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 50'%3E%3Ccircle cx='10' cy='10' r='1' fill='%23fff'/%3E%3Ccircle cx='30' cy='15' r='1' fill='%23fff'/%3E%3Ccircle cx='50' cy='8' r='1' fill='%23fff'/%3E%3Ccircle cx='70' cy='20' r='1' fill='%23fff'/%3E%3Ccircle cx='90' cy='12' r='1' fill='%23fff'/%3E%3C/svg%3E")`,
              backgroundSize: "150px 75px",
            }}
          />

          <div className="container relative py-5 md:py-12">
            <div className="flex flex-col items-center text-center">
              <div className="animate-fade-in-up relative mb-6">
                <div className="bg-primary/20 absolute inset-0 scale-150 rounded-full blur-xl" />
                <img
                  src={logo}
                  alt="Jain Lexicon"
                  className="relative h-16 w-auto object-contain"
                />
              </div>
              <h1
                className="animate-fade-in-up mb-3 font-serif text-3xl font-bold text-white md:text-5xl"
                style={{ animationDelay: "0.1s" }}
              >
                {t("search.title")}
              </h1>
              {query && (
                <p
                  className="animate-fade-in-up mb-8 text-base text-white/70 md:text-lg"
                  style={{ animationDelay: "0.2s" }}
                >
                  {t("search.resultsFor", { query })}
                </p>
              )}
              <div
                className="animate-fade-in-up w-full max-w-lg"
                style={{ animationDelay: "0.3s" }}
              >
                <SearchBar variant="hero" />
              </div>
            </div>
          </div>

          {/* Bottom Wave */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg
              viewBox="0 0 1440 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full"
            >
              <path
                d="M0 40V20C360 35 720 5 1080 20C1260 27.5 1350 32.5 1440 35V40H0Z"
                fill="white"
              />
            </svg>
          </div>
        </section>

        {/* Results */}
        <section className="py-5 md:py-12">
          <div className="container">
            <Button
              variant="ghost"
              onClick={() => navigate("/browse")}
              className="text-muted-foreground hover:text-foreground md:mb-8 mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("search.backToBrowse")}
            </Button>

            {isLoading ? (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="from-secondary/50 to-secondary/30 h-48 animate-pulse rounded-xl bg-gradient-to-br"
                  />
                ))}
              </div>
            ) : error ? (
              <div className="bg-destructive/5 border-destructive/20 rounded-2xl border py-16 text-center">
                <div className="bg-destructive/10 mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full">
                  <SearchIcon className="text-destructive h-10 w-10" />
                </div>
                <h3 className="text-foreground mb-3 font-serif text-xl font-semibold">
                  {t("search.loadErrorTitle")}
                </h3>
                <p className="text-muted-foreground mx-auto mb-8 max-w-md">
                  {t("search.loadErrorBody")}
                </p>
                <Button
                  onClick={() => refetch()}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {t("common.retry")}
                </Button>
              </div>
            ) : terms && terms.length > 0 ? (
              <>
                <div className="bg-primary/10 border-primary/20 md:mb-8 mb-4 flex flex-wrap items-center gap-3 rounded-xl border p-4">
                  <div className="bg-primary/20 flex h-10 w-10 items-center justify-center rounded-full">
                    <SearchIcon className="text-primary h-5 w-5" />
                  </div>
                  <p className="text-foreground min-w-0 flex-1 text-sm sm:text-base">
                    {terms.length === 1
                      ? t("search.foundSingular", { count: terms.length })
                      : t("search.foundPlural", { count: terms.length })}
                  </p>
                </div>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {terms.map((term, i) => (
                    <TermCard
                      key={term.id}
                      id={term.id}
                      word={term.word}
                      meaningEnglish={term.meaning_english}
                      meaningHindi={term.meaning_hindi}
                      meaningGujarati={term.meaning_gujarati}
                      pronunciation={term.pronunciation}
                      index={i}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="from-secondary/30 to-secondary/10 border-border/50 rounded-2xl border bg-gradient-to-br py-16 text-center">
                <div className="bg-muted mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full">
                  <SearchIcon className="text-muted-foreground h-10 w-10" />
                </div>
                <h3 className="text-foreground mb-3 font-serif text-xl font-semibold">
                  {t("search.noResults")}
                </h3>
                <p className="text-muted-foreground mx-auto mb-8 max-w-md">
                  {t("search.noResultsHint")}
                </p>
                <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                  <Button onClick={() => navigate("/browse")} variant="outline">
                    {t("search.browseAll")}
                  </Button>
                  <Button
                    onClick={() => navigate("/contribute")}
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t("common.addTerm")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Search;
